import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../../../hooks/shared';
import { useEventInstanceStore } from '../../../stores';
import { Training } from '../types/calendarTypes';
import ParticipantManagementModal from './ParticipantManagementModal';

interface AdminActionsProps {
  training: Training;
}

const AdminActions: React.FC<AdminActionsProps> = ({ training }) => {
  const { t } = useLanguage();
  const eventInstanceStore = useEventInstanceStore();
  const [showParticipantModal, setShowParticipantModal] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Check if event is in the past
  const isEventPast = training.endDateTime < new Date();

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isMenuOpen]);

  const handleCancelEvent = async () => {
    await eventInstanceStore.cancelEvent(training.id);
    setIsMenuOpen(false);
  };

  const handleConfirmEvent = async () => {
    await eventInstanceStore.confirmEvent(training.id);
    setIsMenuOpen(false);
  };

  const handleParticipantActions = () => {
    setShowParticipantModal(true);
    setIsMenuOpen(false);
  };

  // Hide admin actions if event is in the past
  if (isEventPast) {
    return null;
  }

  return (
    <>
      <div className="relative">
        <button
          ref={buttonRef}
          onClick={(e) => {
            e.stopPropagation();
            setIsMenuOpen((prev) => !prev);
          }}
          className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-white/80 rounded-full transition-all backdrop-blur-sm"
          title={t('calendar.participantActions')}
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
            />
          </svg>
        </button>

        {isMenuOpen && (
          <div
            ref={menuRef}
            className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[200px] z-50"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleParticipantActions();
              }}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              <span className="text-blue-600">👥</span>
              <span>{t('calendar.participantActions')}</span>
            </button>

            {training.status === 'Cancelled' ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleConfirmEvent();
                }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2 border-t border-gray-200"
              >
                <span className="text-green-600">✓</span>
                <span>{t('training.confirmEvent')}</span>
              </button>
            ) : (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancelEvent();
                }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2 border-t border-gray-200"
              >
                <span className="text-orange-600">✕</span>
                <span>{t('training.cancelEvent')}</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Participant management modal */}
      <ParticipantManagementModal
        training={training}
        isOpen={showParticipantModal}
        onClose={() => setShowParticipantModal(false)}
      />
    </>
  );
};

export default AdminActions;
